
import * as React from 'react';
import { Button } from '@/components/ui/button';
import { Printer, Loader2, FileText } from 'lucide-react';
import { Order } from '@/types';
import { useToast } from '@/hooks/use-toast';
import { formatOrderForThermalPrinting, printReceipt } from '@/services/printService';

interface OrderReprintButtonProps {
  order: Order;
  size?: 'default' | 'sm' | 'lg' | 'icon';
  variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
  className?: string;
  faturar?: boolean;
  onViewDanfe?: (orderId: number) => void;
}

const OrderReprintButton: React.FC<OrderReprintButtonProps> = ({
  order,
  size = 'sm',
  variant = 'outline',
  className = '',
  faturar = false,
  onViewDanfe
}) => {
  const [isPrinting, setIsPrinting] = React.useState(false);
  const { toast } = useToast();
  
  const handleReprint = async () => {
    setIsPrinting(true);
    
    try {
      // Get company information from localStorage for the receipt header
      const companyName = localStorage.getItem('empresa_nome') || 'RSP Cloud App';
      const userName = localStorage.getItem('user_name') || 'ATENDENTE';
      
      // Ensure we have customer information properly included
      // Improved customer name detection with better fallbacks
      const customerName = order.customerName || 
                          (order.customerId ? `Cliente #${order.customerId}` : 'Consumidor');
      
      // Prepare order data with complete information for printing
      const orderForPrinting = {
        ...order,
        // Ensure we have customer and vendor information with proper fallbacks
        customerName: customerName,
        waiterName: userName,
        // Include company information for header
        companyName: companyName,
        // Ensure we have correct payment information and preserve VendaFormaPagamento
        paymentMethodId: order.paymentMethodId,
        receivedAmount: order.receivedAmount || order.totalAmount,
        changeAmount: order.changeAmount || 0,
        // Explicitly pass VendaFormaPagamento for payment details
        VendaFormaPagamento: order.VendaFormaPagamento || []
      };
      
      console.log('Preparing print with data:', orderForPrinting);
      console.log('Customer name for receipt:', customerName);
      console.log('Payment methods:', orderForPrinting.VendaFormaPagamento);
      
      // Format the order for printing
      const formattedReceipt = formatOrderForThermalPrinting(orderForPrinting);
      
      // Send to printing service
      await printReceipt(formattedReceipt);
      
      toast({
        title: "Reimpressão enviada",
        description: `Pedido #${order.id.substring(0, 8)} enviado para impressão${faturar ? ' com faturamento automático' : ''}`,
      });
    } catch (error) {
      console.error("Erro ao reimprimir pedido:", error);
      toast({
        title: "Erro na reimpressão",
        description: error instanceof Error ? error.message : "Erro ao enviar para a impressora",
        variant: "destructive"
      });
    } finally {
      setIsPrinting(false);
    }
  };
  
  const handleViewDanfe = () => {
    if (order.erpId && onViewDanfe) {
      onViewDanfe(order.erpId);
    } else {
      toast({
        title: "DANFE não disponível",
        description: "Este pedido não possui um ID ERP associado para visualizar o DANFE",
        variant: "destructive"
      });
    }
  };
  
  return (
    <div className="flex items-center gap-2">
      <Button
        variant={variant}
        size={size}
        className={`flex items-center gap-1 ${className}`}
        onClick={handleReprint}
        disabled={isPrinting}
      >
        {isPrinting ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : (
          <Printer className="h-3 w-3" />
        )}
        <span>Reimprimir</span>
      </Button>

      {order.erpId && onViewDanfe && (
        <Button
          variant="secondary"
          size={size}
          className="flex items-center gap-1"
          onClick={handleViewDanfe}
        >
          <FileText className="h-3 w-3" />
          <span>DANFE</span>
        </Button>
      )}
    </div>
  );
};

export default OrderReprintButton;
